/**
 * Shared drag state, refs, sensors, and scroll-lock helpers for Kanban DnD.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import {
  createCollisionDetection,
  lockPageScroll,
  unlockPageScroll,
  cleanupScrollLock,
  findColumnForTask,
  getTaskPositionIndex,
} from '../../utils';

const useKanbanDragState = ({
  columnIds,
  taskIdsByColumn,
  tasksByStatus,
  boardRef,
}) => {
  const [activeColumnId, setActiveColumnId] = useState(null);
  const [activeCardId, setActiveCardId] = useState(null);
  const [frozenTaskCounts, setFrozenTaskCounts] = useState(null);
  const [frozenHiddenCounts, setFrozenHiddenCounts] = useState(null);
  const [visibleCardCount, setVisibleCardCount] = useState(8);
  const [activeCardSwimlaneId, setActiveCardSwimlaneId] = useState(null);

  const savedScrollRef = useRef(null);
  const scrollBlockerRef = useRef(null);
  const dropIndexRef = useRef(null);
  const dropColumnRef = useRef(null);
  const lastStatusChangeRef = useRef({ taskId: null, columnId: null });

  const doLockPageScroll = useCallback(() => {
    lockPageScroll(scrollBlockerRef);
  }, []);

  const doUnlockPageScroll = useCallback(() => {
    unlockPageScroll(scrollBlockerRef);
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const findColumn = useCallback(
    (taskId) => findColumnForTask(taskId, taskIdsByColumn),
    [taskIdsByColumn],
  );

  const getPositionIndex = useCallback(
    (taskId, columnId) => getTaskPositionIndex(taskId, columnId, tasksByStatus),
    [tasksByStatus],
  );

  const saveScrollPositions = useCallback(() => {
    savedScrollRef.current = {
      windowY: window.scrollY,
      boardTop: boardRef?.current?.scrollTop ?? 0,
      boardLeft: boardRef?.current?.scrollLeft ?? 0,
    };
  }, [boardRef]);

  const restoreScrollPositions = useCallback(() => {
    if (!savedScrollRef.current) return;

    const saved = savedScrollRef.current;
    savedScrollRef.current = null;

    requestAnimationFrame(() => {
      window.scrollTo({ top: saved.windowY, behavior: 'instant' });
      if (boardRef?.current) {
        boardRef.current.scrollTop = saved.boardTop;
        boardRef.current.scrollLeft = saved.boardLeft;
      }
    });
  }, [boardRef]);

  const resetDragState = useCallback(() => {
    setActiveColumnId(null);
    setActiveCardId(null);
    setActiveCardSwimlaneId(null);
    setFrozenTaskCounts(null);
    setFrozenHiddenCounts(null);
    dropIndexRef.current = null;
    dropColumnRef.current = null;
    lastStatusChangeRef.current = { taskId: null, columnId: null };
  }, []);

  const unlockScrollAndRestore = useCallback(() => {
    if (activeColumnId || activeCardId) {
      doUnlockPageScroll();
      restoreScrollPositions();
    }
  }, [activeColumnId, activeCardId, doUnlockPageScroll, restoreScrollPositions]);

  const collisionDetection = useMemo(() => {
    const activeType = activeColumnId ? 'column' : (activeCardId ? 'card' : null);
    return createCollisionDetection(activeType, columnIds, activeCardSwimlaneId);
  }, [activeColumnId, activeCardId, columnIds, activeCardSwimlaneId]);

  const autoScrollConfig = useMemo(() => {
    if (activeColumnId || activeCardId) return false;
    return {
      enabled: true,
      threshold: { x: 0.1, y: 0.1 },
    };
  }, [activeColumnId, activeCardId]);

  useEffect(() => {
    return () => {
      savedScrollRef.current = null;
      cleanupScrollLock(scrollBlockerRef);
    };
  }, []);

  return {
    activeColumnId,
    setActiveColumnId,
    activeCardId,
    setActiveCardId,
    activeCardSwimlaneId,
    setActiveCardSwimlaneId,
    frozenTaskCounts,
    setFrozenTaskCounts,
    frozenHiddenCounts,
    setFrozenHiddenCounts,
    visibleCardCount,
    setVisibleCardCount,
    dropIndexRef,
    dropColumnRef,
    lastStatusChangeRef,
    sensors,
    collisionDetection,
    autoScrollConfig,
    findColumn,
    getPositionIndex,
    saveScrollPositions,
    doLockPageScroll,
    resetDragState,
    unlockScrollAndRestore,
  };
};

export default useKanbanDragState;
